import PropTypes from "prop-types"
import { useRef, useState } from "react"
import styled from "styled-components"
import Button from "../button/Button"
import useCleanupInterval from "../hooks/useCleanupInterval"
import { useTranslate } from "../lng/withTranslate"
import copyToClipboard from "./copyToClipboard"

const Container = styled.div`
  position: relative;
  display: inline-block;

  > div:first-child {
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
    top: -100%;
  }
`

// displays a Copy button above children that will
// copy the provided text or the textContent of the children
const Copy = ({ children, text, ...buttonProps }) => {
  const { t } = useTranslate()
  const buttonContainerRef = useRef(null)
  const [showCopy, setShowCopy] = useState(false)
  const [copying, setCopying] = useState(false)
  const cleanupLabelInterval = useCleanupInterval()
  const cleanupMouseLeaveInterval = useCleanupInterval()

  const { icon, label } = copying
    ? {
        icon: "check",
        label: t("Copied!")
      }
    : {
        icon: "copy",
        label: t("Copy")
      }

  const handleCopy = (e) => {
    e.stopPropagation()
    text = text || buttonContainerRef.current.nextSibling.textContent
    copyToClipboard(text)
    // successfully copied label
    setCopying(true)
    // reset label
    cleanupLabelInterval(setTimeout(() => setCopying(false), 1000))
    // simulating a clearing after mouseleave (in the case of handleCopy being triggered
    // in a touch screen only environment)
    cleanupMouseLeaveInterval(setTimeout(() => setShowCopy(false), 1000))
  }

  // prevent hiding Button after entering target element
  const handleShowCopy = () => {
    clearInterval(cleanupMouseLeaveInterval.intervalRef.current)
    if (!showCopy) setShowCopy(true)
  }

  // hide Button after leaving target element
  const handleHideCopy = () => {
    cleanupMouseLeaveInterval(setTimeout(() => setShowCopy(false), 150))
  }

  // allowing for copying by clicking the Container as a convenience
  const handleTouch = (e) => {
    setShowCopy(true)
    handleCopy(e)
  }

  return (
    <Container
      onClick={handleTouch}
      // using onMouseOver instead of onMouseEnter as leaving the Button
      // may not trigger an onMouseEnter on the Container, failing to cancel out the
      // effect of the Button's onMouseLeave
      onMouseOver={handleShowCopy}
      onMouseLeave={handleHideCopy}
    >
      <div ref={buttonContainerRef}>
        {showCopy && (
          <Button
            icon={icon}
            label={label}
            onClick={handleCopy}
            onMouseEnter={handleShowCopy}
            onMouseLeave={handleHideCopy}
            styleType="primary"
            {...buttonProps}
          />
        )}
      </div>
      {children}
    </Container>
  )
}

Copy.displayName = "Copy"

Copy.propTypes = {
  // optionally pass text to be copied, otherwise
  // textContent of children will be copied
  text: PropTypes.string
}

export default Copy
